import React, { useEffect, useState } from 'react';
import { trackingAPI } from '../../services/api';

// Right-hand column of the booking detail modal: the shipment's tracking.
//
// - The shipment's own history (tracking_events on the booking): what our team
//   records and what the customer sees on the tracking page. Already in the
//   booking detail, so it shows at once.
// - Carrier scans from the rate platform for the AWB, when it has any. Our own
//   SendMyParcel shipments usually have none there ("Not Found"); carrier AWBs
//   such as DHL or FedEx may.

const NO_DATA = ['Not Found', 'Error', 'Unknown'];

const when = (e) => {
  const t = new Date(e.timestamp || e.created_at || e.event_time || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// The platform sends "30-Oct-2026 @ 08:57".
const platformTime = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace('@', ' '));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const Timeline = ({ items, testId }) => (
  <ol className="relative ml-1.5 border-l border-gray-200" data-test={testId}>
    {items.map((e, i) => (
      <li key={e.key} className="relative pb-5 pl-5 last:pb-0">
        <span
          className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${i === 0 ? 'bg-orange-500' : 'bg-gray-300'}`}
          aria-hidden="true"
        />
        <p className={`text-sm font-semibold ${i === 0 ? 'text-gray-900' : 'text-gray-700'}`}>{e.title}</p>
        {e.description ? <p className="mt-0.5 text-sm text-gray-600 break-words">{e.description}</p> : null}
        <p className="mt-0.5 text-xs text-gray-500">{[e.location, e.time].filter(Boolean).join(' · ')}</p>
      </li>
    ))}
  </ol>
);

// showHeader: off where the surrounding pop-up already names the shipment.
const BookingTrackingPanel = ({ d, fmtDateTime, titleCase, showHeader = true }) => {
  const awb = d?.carrier_info?.tracking_number || null;
  const [scans, setScans] = useState({ state: 'idle' });

  useEffect(() => {
    if (!awb) {
      setScans({ state: 'none' });
      return undefined;
    }
    let live = true;
    setScans({ state: 'loading' });
    trackingAPI.trackCombined(awb)
      .then((res) => {
        const pc = res?.priority_connections_tracking?.data;
        const list = pc && !NO_DATA.includes(pc.status) ? pc.tracking_events || [] : [];
        if (live) setScans(list.length ? { state: 'ok', status: pc.status, location: pc.current_location, list } : { state: 'empty' });
      })
      .catch(() => { if (live) setScans({ state: 'error' }); });
    return () => { live = false; };
  }, [awb]);

  const history = [...(d?.tracking_events || [])]
    .sort((a, b) => when(b) - when(a))
    .map((e, i) => ({
      key: e.id || `h-${i}`,
      title: titleCase(e.status),
      description: (e.description || '').trim(),
      location: (e.location || '').replace(/\s+/g, ' ').trim(),
      time: fmtDateTime(e.timestamp || e.created_at || e.event_time),
    }));

  const carrierItems = scans.state === 'ok'
    ? scans.list.map((e, i) => ({
      key: `c-${i}`,
      title: e.status || 'Update',
      description: e.message && e.message !== e.status ? e.message : '',
      location: e.location && e.location !== 'N/A' ? e.location : '',
      time: platformTime(e.timestamp) ? fmtDateTime(platformTime(e.timestamp)) : e.timestamp,
    }))
    : [];

  return (
    <div className="space-y-6" data-test="booking-tracking">
      {showHeader && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Tracking</h4>
          <p className="mt-1 text-sm text-gray-600">
            {`AWB ${awb || 'not assigned yet'} · ${titleCase(d?.status)}`}
          </p>
        </div>
      )}

      <section>
        <h5 className="mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Shipment history</h5>
        {history.length ? (
          <Timeline items={history} testId="tracking-history" />
        ) : (
          <p className="text-sm text-gray-500">No tracking updates yet.</p>
        )}
      </section>

      <section>
        <h5 className="mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Carrier scans</h5>
        {scans.state === 'loading' && <p className="text-sm text-gray-500">Checking with the carrier&hellip;</p>}
        {scans.state === 'none' && <p className="text-sm text-gray-500">No AWB yet, so nothing to check with the carrier.</p>}
        {scans.state === 'empty' && <p className="text-sm text-gray-500" data-test="carrier-scans-empty">The carrier has no scans for this AWB.</p>}
        {scans.state === 'error' && <p className="text-sm text-gray-500">Couldn&rsquo;t reach the carrier tracking just now.</p>}
        {scans.state === 'ok' && (
          <>
            {scans.location && scans.location !== 'N/A' ? (
              <p className="mb-3 text-sm text-gray-600">{`${scans.status} · ${scans.location}`}</p>
            ) : null}
            <Timeline items={carrierItems} testId="carrier-scans" />
          </>
        )}
      </section>
    </div>
  );
};

export default BookingTrackingPanel;
